import React, { Suspense, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { wrapPromise } from "go-core";
import Header from "go-app/components/Header";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import ReportData from "go-report/reports";
import { getReportCustomFieldsResources, reportParamsConverter } from "go-report/reports/services/report-service";
import { getSelectedSegmentForReportConfig } from "go-report/reports/services/segment-service";
import { ReportConfig } from "go-report/reports/services/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { adminApi } from "../../../../../../services/Api/Admin/adminApi";
import ReportOrganizationUtils from "../../../../components/Admin/services/ReportOrganizationUtils";

interface State {
	resource?: Record<string, any>;
}

const listName = "ADMIN_ORGANIZATION_REPORT";
const resourceType = "ORGANIZATION_REPORT";

const Report = ({ resource }: State) => {
	const { t } = useTranslation(["translation", "admin", "lib"]);
	const segmentContextValue = React.useContext(SegmentContext);
	const configId = "admin_organization_report";

	if (!resource) return null;
	const data = resource.read();

	let config = {
		reportConfigId: configId,
		aggregatePrefix: "summary",
		selectedColumns: ReportOrganizationUtils.getSelectedColumns(configId),
		groups: ReportOrganizationUtils.getGroups(t),
		showPercent: true,
		columns: ReportOrganizationUtils.getColumns(t),
		selectedGroups: ["NONE"],
		groupsInfo: ReportOrganizationUtils.getGroupsInfo(t),
		filters: [
			...ReportOrganizationUtils.getFilters(t),
			{
				id: "count",
				name: "Ilość",
				type: "number",
			},
		],
		segments: [
			{
				id: "all",
				name: `${t("common.word.all", { ns: "lib" })}`,
				slug: "all",
				columns: ReportOrganizationUtils.getSelectedColumns(configId),
				filters: [],
				groups: [],
			},
		],
		selectedSegment: getSelectedSegmentForReportConfig(data.segments, "all"),
		fetch: async (params: Record<string, any> = {}, sourceToken: CancelTokenSource) => {
			return Promise.all([
				adminApi.getAdminOrganizationReport(reportParamsConverter(params), { cancelToken: sourceToken?.token }),
			]);
		},
		saveSegment: async (segment: SegmentType) => {
			return segmentContextValue.save(listName, resourceType, segment);
		},
		exportConfig: {
			title: t("modules.organization_report.header.title"),
			filename: t("modules.organization_report.field.export_config.filename"),
		},
		doNotUseDefaultDateRangeInFilters: true,
	} as ReportConfig;

	data.fields = getReportCustomFieldsResources(data.fields, ["ORGANIZATION", "PARTNER"], t);

	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		filterValues: data.filter_values,
	};

	return <ReportData config={config} />;
};

const AdminOrganizationReportPage = () => {
	const [resource, setResource] = useState<Record<string, any>>();
	const { t } = useTranslation("admin");
	const segmentContextValue = React.useContext(SegmentContext);
	const customFieldResourceTypes = [
		{
			type: "ORGANIZATION",
			name: t(`enums.custom_fields.resources.ORGANIZATION`),
		},
		{
			type: "PARTNER",
			name: t(`enums.custom_fields.resources.PARTNER`),
		},
	];
	const isMobile = useWindowSize().isMobile;
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		handleChangeTabTitle(
			`${t("modules.organization_report.header.title")} | ${t("lib:go_component.navigation.admin")}`
		);
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType, customFieldResourceTypes)));
	}, []);

	return (
		<div className={"content"}>
			{!isMobile && <Header title={t("modules.organization_report.header.title")} />}
			<Suspense fallback={<LoadingContainer />}>
				<Report resource={resource} />
			</Suspense>
		</div>
	);
};

export default AdminOrganizationReportPage;
